import { authentication } from '@microsoft/teams-js';
import type {
  ConnectorId,
  ConnectorPermissionLevel,
} from '@research-assistant/shared';
import { supportsConnectorWrite } from '@research-assistant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { notify } from '../../lib/notify.js';
import * as api from '../services/integration-api.js';

import { useInvalidateConnections } from './useConnections.js';

const POLL_MAX_ATTEMPTS = 60;
const POLL_INTERVAL_MS = 1000;

/** What one look at a connect learned: done, refused, or not yet. */
type ConnectProgress = 'connected' | 'failed' | 'pending';

/** What a fresh connect or a reconnect waits for when it has to read presence. */
interface PresenceTarget {
  provider: ConnectorId;
  workspaceId: string | null;
  requestedLevel: ConnectorPermissionLevel | undefined;
  requestedAllowDelete: boolean | undefined;
  connectionId: string | null;
  knownConnectionIds: ReadonlySet<string>;
  personalWorkspace: boolean;
}

/**
 * Reads the attempt identity named when the connect started. This is the answer the wait trusts: the
 * callback settles the attempt with the connection it wrote, so nothing has to be inferred. A refusal
 * is said out loud, because the callback page that showed it has already closed itself.
 */
async function readAttempt(
  attemptId: string,
  current: () => boolean,
): Promise<ConnectProgress> {
  const attempt = await api.getConnectAttempt(attemptId);
  if (attempt.status === 'failed' && current()) {
    notify.error(attempt.message ?? 'The connection was not completed.');
  }
  return attempt.status;
}

/**
 * The fallback for an identity that names no attempt: looks for the grant this connect asked for in
 * presence. It infers the new connection, which is why it is only the fallback — the inference has
 * broken every time presence changed meaning.
 *
 * It reads the entry's CONNECTIONS, never its flat `resolved_from` / `permission_level`, which answer
 * about whichever connection the server resolved first.
 *
 * - A RECONNECT names its `connectionId`, and only that connection counts.
 * - A fresh connect counts a connection the caller did not list in `knownConnectionIds` — without the
 *   list a second connection would finish on the first one. Its lane must be the one a fresh connect
 *   writes: `workspace` in a working workspace, `personal` in the caller's own personal space, where
 *   identity reports the connection it just wrote as `personal`. A personal connection seen from a
 *   working workspace is inherited, never new.
 *
 * A connect that asked for `read_write` also waits for that level, and one that asked for deletes waits
 * until the connection stops saying `allow_delete: false`, since a reconnect runs against a connection
 * that already exists with less. A presence read that omits `allow_delete` (an older identity) does not
 * hold the wait. A connection still marked `needsReconnect` never counts, since reconnecting a dead one
 * finds it in presence before the sign-in finishes.
 */
async function readPresence(target: PresenceTarget): Promise<ConnectProgress> {
  const presence = await api.getConnectorPresence(target.workspaceId);
  const connections = presence.providers[target.provider]?.connections ?? [];
  const freshLane = target.personalWorkspace ? 'personal' : 'workspace';
  const authorized = target.connectionId
    ? connections.find(
        (connection) => connection.connectionId === target.connectionId,
      )
    : connections.find(
        (connection) =>
          connection.scope === freshLane &&
          !target.knownConnectionIds.has(connection.connectionId),
      );
  const writeGranted =
    target.requestedLevel !== 'read_write' ||
    authorized?.permissionLevel === 'read_write';
  const deleteGranted =
    target.requestedAllowDelete !== true || authorized?.allowDelete !== false;
  return authorized &&
    writeGranted &&
    deleteGranted &&
    authorized.needsReconnect !== true
    ? 'connected'
    : 'pending';
}

/**
 * Looks at the connect once a second until it is connected or refused, the hook unmounts, or the
 * sign-in window is gone. A closed window gets one last look — the callback page closes itself right
 * after it settled the connect — and then the wait ends instead of holding the dialog for the whole
 * timeout. A failed look counts as "not yet".
 */
async function waitForConnect(
  look: () => Promise<ConnectProgress>,
  alive: { current: boolean },
  windowClosed: () => boolean,
): Promise<boolean> {
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
    if (!alive.current) return false;
    const closedBeforeLook = windowClosed();
    const progress = await look().catch((): ConnectProgress => 'pending');
    if (!alive.current) return false;
    if (progress === 'connected') return true;
    if (progress === 'failed' || closedBeforeLook) return false;
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  return false;
}

/**
 * Headless OAuth popup driver for the Connectors Directory. Mirrors the
 * popup half of `AuthCard.tsx` but **without** the in-flight-chat retry
 * machinery (`markAssistantPending` / `streamRetryResponse`) — the directory
 * is opened from settings, not from a paused chat turn, so we only need to
 * open the popup and invalidate the connections query when the server-side
 * status flips.
 *
 * Returns `{ connect, busy }`. `connect()` resolves when polling sees the
 * provider become connected or the popup is dismissed.
 */
export function useOAuthConnect(
  provider: ConnectorId,
  workspaceId?: string | null,
  options?: { explainUnavailableActions?: boolean },
) {
  const [busy, setBusy] = useState(false);
  const activeRef = useRef(false);
  const owner = `${provider}:${workspaceId ?? ''}`;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const explainUnavailableActions = options?.explainUnavailableActions ?? false;
  /**
   * The service's own refusal of the last authorize, with its status — `null` once a connect starts.
   * `connect()` still answers a plain boolean, because that is all its callers act on; the refusal is
   * kept beside it for the one surface that can DO something about a particular status, which today is
   * the 409 the connect form fixes by naming both connections.
   */
  const [error, setError] = useState<{
    status: number;
    message: string;
  } | null>(null);
  const aliveRef = useRef(true);
  /** Set by {@link cancel}: the person dismissed the dialog, so this connect stops waiting. */
  const cancelledRef = useRef(false);
  /** The browser sign-in window of the connect in flight, so {@link cancel} can close it. */
  const popupRef = useRef<Window | null>(null);
  const invalidate = useInvalidateConnections();
  const queryClient = useQueryClient();

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      cancelledRef.current = true;
      try {
        popupRef.current?.close();
      } catch {
        /** The sign-in window may already be closed. */
      }
    };
  }, [provider, workspaceId]);

  const connect = useCallback(
    async (opts?: {
      permissionLevel?: ConnectorPermissionLevel;
      /** The Allow delete switch the access dialog confirmed. */
      allowDelete?: boolean;
      /**
       * The connection to RE-connect. Given, the same connection keeps its OAuth application and its
       * tenant instead of a second one being created, and the poll waits for THAT connection's grant.
       */
      connectionId?: string | null;
      /**
       * The customer directory to connect. Given, the sign-in runs against the multi-tenant
       * application and the connection it creates belongs to that directory; the service refuses a
       * directory whose administrator has not approved us.
       */
      externalTenantId?: string | null;
      /** What to call the new connection, carried through the OAuth round trip so it is born named. */
      label?: string;
      /** The agent instruction for the new connection, carried through the same round trip. */
      usageHint?: string;
      /**
       * The connections of this provider the caller can already see. A fresh connect is only finished
       * once one appears beyond them — without this the poll would accept a connection that was there
       * all along and close the popup mid sign-in.
       */
      knownConnectionIds?: readonly string[];
      /**
       * Whether the connect runs in the caller's own personal space, where the connection it writes is
       * reported as `personal`. Only the presence fallback reads it.
       */
      personalWorkspace?: boolean;
    }): Promise<boolean> => {
      if (activeRef.current) {
        if (explainUnavailableActions)
          notify.warning(
            'Sign-in is already running. Finish it in the sign-in window, or cancel it before trying again.',
          );
        return false;
      }
      activeRef.current = true;
      const ownsAttempt = () =>
        aliveRef.current && ownerRef.current === owner && !cancelledRef.current;
      setBusy(true);
      setError(null);
      cancelledRef.current = false;
      popupRef.current = null;
      try {
        let authUrl: string;
        let attemptId: string | null;
        const connectionId = opts?.connectionId ?? null;
        /**
         * Read-only providers always request read access and no deletes. Other providers send the
         * access the person chose; left out, the authorize URL names none and identity applies its own rule.
         */
        const writable = supportsConnectorWrite(provider);
        const permissionLevel = writable ? opts?.permissionLevel : 'read';
        const allowDelete = writable ? opts?.allowDelete : false;
        try {
          const resp = await api.getAuthUrl(
            provider,
            {
              permissionLevel,
              allowDelete,
              connectionId,
              ...(opts?.externalTenantId
                ? { externalTenantId: opts.externalTenantId }
                : {}),
              ...(opts?.label === undefined ? {} : { label: opts.label }),
              ...(opts?.usageHint === undefined
                ? {}
                : { usageHint: opts.usageHint }),
            },
            workspaceId,
          );
          authUrl = resp.auth_url;
          attemptId = resp.attempt_id ?? null;
          if (!ownsAttempt()) {
            if (ownerRef.current === owner) invalidate();
            return false;
          }
        } catch (err) {
          /** A refusal for a dismissed connect is not shown: a 409 would reopen the form it closed. */
          if (!ownsAttempt()) return false;
          if (err instanceof api.ConnectorRequestError) {
            setError({ status: err.status, message: err.message });
          } else if (err instanceof Error) {
            setError({ status: 0, message: err.message });
          }
          return false;
        }

        /**
         * The browser popup, held so the wait can see it close and so the opener can close it: Chrome
         * sometimes drops the opener relationship across a cross-origin redirect, and a window.close()
         * from inside the callback page then silently no-ops. In Teams there is no handle to watch.
         */
        let popupHandle: Window | null = null;
        let popupOpened = false;
        const target: PresenceTarget = {
          provider,
          workspaceId: workspaceId ?? null,
          requestedLevel: permissionLevel,
          requestedAllowDelete: allowDelete,
          connectionId,
          knownConnectionIds: new Set(opts?.knownConnectionIds ?? []),
          personalWorkspace: opts?.personalWorkspace ?? false,
        };
        const watching = {
          get current(): boolean {
            return ownsAttempt();
          },
        };
        const watch = waitForConnect(
          () =>
            attemptId
              ? readAttempt(attemptId, ownsAttempt)
              : readPresence(target),
          watching,
          () => popupOpened && (popupHandle === null || popupHandle.closed),
        );

        try {
          await authentication.authenticate({
            url: authUrl,
            width: 600,
            height: 700,
          });
        } catch {
          /**
           * Not in Teams — open a regular popup. The wait drives state. A connect cancelled while the
           * Teams check was still deciding opens nothing, but still falls through to the re-read below:
           * a Teams sign-in can finish before its window closes.
           */
          if (ownsAttempt()) {
            popupHandle = window.open(
              authUrl,
              `${provider}_auth`,
              'popup,width=600,height=700',
            );
            popupOpened = true;
            popupRef.current = popupHandle;
          }
        }

        const ok = await watch;
        if (popupHandle && !popupHandle.closed) {
          try {
            popupHandle.close();
          } catch {
            /* popup may have already been closed by its own script */
          }
        }
        /**
         * Re-read the connections whatever the outcome: a wait that ended unconfirmed may still have
         * connected, and without this the card says "not connected" until somebody reloads the page.
         */
        if (ownerRef.current !== owner) return false;
        invalidate();
        if (ok) {
          /**
           * A fresh connect can grant read_write; permission_level lives on the
           * catalog query too, so refresh it rather than keep a stale 'read'.
           */
          void queryClient.invalidateQueries({
            queryKey: ['connectors-catalog'],
          });
        }
        return ok;
      } finally {
        activeRef.current = false;
        if (aliveRef.current) setBusy(false);
      }
    },
    [
      provider,
      workspaceId,
      owner,
      explainUnavailableActions,
      invalidate,
      queryClient,
    ],
  );

  /** Clears the last refusal, so a form that showed it can be reopened clean. */
  const clearError = useCallback(() => setError(null), []);

  /**
   * Stops the connect in flight and closes its sign-in window. The window slips behind the browser on
   * the first click back into the page, and dismissing the dialog is then the only way out a person
   * can think of. A sign-in that already finished still counts: the connections are re-read either way.
   */
  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (explainUnavailableActions && activeRef.current)
      notify.warning(
        'Sign-in canceled. If the provider already accepted it, refresh the connection status before trying again.',
      );
    try {
      popupRef.current?.close();
    } catch {
      /* the window may already be gone */
    }
  }, [explainUnavailableActions]);

  return { connect, busy, error, clearError, cancel };
}
